import { Customer } from '../types';
import { findSimilarCustomers, nameSimilarity, normalizeCompanyName } from './customerDuplicates';
import { ImportedCustomerRow, normNumber, numberTakenByOther } from './customerListImport';
import { NumberRange, rangeForName } from './customerNumberRanges';
import { describeError } from './errorMessage';

/**
 * Kunden-Uebernahme (Wizard): Unternehmen aus den Projekten UND die
 * Kundenliste der Steuerkanzlei in EINEM Abgleich zusammenfuehren, bevor
 * irgendetwas angelegt wird.
 *
 * Pur bis auf executeOnboardingPlan, das seine Schreib-Operationen injiziert
 * bekommt — Supabase, Datei-Parser und React liegen bei den Aufrufern
 * (Standing Decision: Logik in puren Funktionen, kein jsdom im Projekt).
 *
 * Leitregeln:
 *  - NICHTS wird angelegt, bevor der Nutzer den Plan bestaetigt hat.
 *  - Es wird nie geraten: nur EXAKT gleiche Normalnamen (oder ein
 *    Kundennummern-Treffer) werden zusammengefuehrt. Aehnliche Zeilen bleiben
 *    getrennt und werden zurueckgestellt — ein falscher Auto-Merge kostet eine
 *    Kundennummer und ist nicht mehr sauber trennbar.
 *  - Die Nummer der Steuerkanzlei ist autoritativ: sie gewinnt vor der
 *    automatischen Vergabe und wird bei einem Treffer auch auf den
 *    Bestandskunden uebernommen.
 */

// ─── Projekt-Kandidaten ──────────────────────────────────────────────────────

/** Rolle, in der das Unternehmen am Projekt haengt. */
export type CompanyRole = 'auftraggeber' | 'bauherr';

export interface ProjectCompanyOrigin {
    projectId: string;
    projectNumber?: string;
    projectName?: string;
    role: CompanyRole;
    /**
     * Das Projekt haengt noch an keinem Kunden und darf darum verknuepft
     * werden. Ist bereits ein customer_id gesetzt, wird er NIE ueberschrieben:
     * customer_id ist die Rechnungsbeziehung, und ein Bauherr, der zufaellig
     * mit uebernommen wird, darf sie nicht umbiegen.
     */
    linkable: boolean;
}

export interface ProjectCompanyCandidate {
    /** normalizeCompanyName des Namens — Schluessel des Abgleichs. */
    key: string;
    name: string;
    address?: string;
    contactPerson?: string;
    phone?: string;
    email?: string;
    origins: ProjectCompanyOrigin[];
}

const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

const completeness = (v: {
    address?: string;
    contactPerson?: string;
    phone?: string;
    email?: string;
}): number =>
    (v.address ? 1 : 0) + (v.contactPerson ? 1 : 0) + (v.phone ? 1 : 0) + (v.email ? 1 : 0);

interface RoleReading {
    role: CompanyRole;
    name: string;
    address?: string;
    contactPerson?: string;
    phone?: string;
    email?: string;
}

/**
 * BEIDE Rollen eines Projekts lesen — Auftraggeber UND Bauherr.
 *
 * Bewusst NICHT extractCustomerCandidates (crmMatching.ts): das dortige
 * Auto-Matching nimmt nur den Auftraggeber (Bauherr nur als Fallback) und
 * bleibt unveraendert. Fuer die Uebernahme sind aber alle Unternehmen
 * gefragt, "die bei den Projekten sind".
 */
const readRoles = (meta: Record<string, unknown>): RoleReading[] => {
    const roles: RoleReading[] = [];
    const customerName = str(meta.customerName);
    if (customerName) {
        roles.push({
            role: 'auftraggeber',
            name: customerName,
            address: str(meta.customerAddress) || undefined,
            contactPerson: str(meta.customerContactPerson) || undefined,
            phone: str(meta.customerPhone) || undefined,
            email: str(meta.customerEmail) || undefined,
        });
    }
    const clientName = str(meta.clientName);
    if (clientName) {
        roles.push({
            role: 'bauherr',
            name: clientName,
            address: str(meta.clientAddress) || undefined,
            contactPerson: str(meta.clientContactPerson) || undefined,
            phone: str(meta.clientPhone) || undefined,
            email: str(meta.clientEmail) || undefined,
        });
    }
    return roles;
};

/**
 * Unternehmen aus Projekt-Rows, gruppiert nach normalisiertem Namen.
 *
 * Ein Auftraggeber eines bereits verknuepften Projekts wird UEBERSPRUNGEN —
 * der steht schon im CRM. Ein Bauherr desselben Projekts bleibt sichtbar
 * (customer_id sagt nichts ueber ihn aus), kann aber nicht mehr verknuepft
 * werden (linkable: false).
 */
export const extractProjectCompanies = (projects: unknown[]): ProjectCompanyCandidate[] => {
    const byKey = new Map<string, ProjectCompanyCandidate & { _best: number }>();

    for (const raw of projects || []) {
        if (!raw || typeof raw !== 'object') continue;
        const row = raw as Record<string, any>;
        const meta = (row.data?.metadata ?? row.metadata ?? {}) as Record<string, unknown>;
        const projectId = String(row.id ?? '');
        if (!projectId) continue;
        const linked = Boolean(row.customer_id);
        const projectNumber = str(row.project_number) || undefined;
        const projectName = str(row.name) || undefined;

        for (const reading of readRoles(meta)) {
            // Auftraggeber eines verknuepften Projekts: bereits im CRM.
            if (linked && reading.role === 'auftraggeber') continue;
            const key = normalizeCompanyName(reading.name);
            if (!key) continue;

            const origin: ProjectCompanyOrigin = {
                projectId,
                projectNumber,
                projectName,
                role: reading.role,
                linkable: !linked,
            };
            const fields = completeness(reading);
            const existing = byKey.get(key);
            if (!existing) {
                byKey.set(key, {
                    key,
                    name: reading.name,
                    address: reading.address,
                    contactPerson: reading.contactPerson,
                    phone: reading.phone,
                    email: reading.email,
                    origins: [origin],
                    _best: fields,
                });
                continue;
            }
            // Dasselbe Projekt in beiden Rollen (Bauherr = Auftraggeber):
            // nur EINE Herkunft, der Auftraggeber gewinnt.
            const sameProject = existing.origins.find((o) => o.projectId === projectId);
            if (sameProject) {
                if (sameProject.role === 'bauherr' && reading.role === 'auftraggeber') {
                    sameProject.role = 'auftraggeber';
                }
            } else {
                existing.origins.push(origin);
            }
            // Vollstaendigste Variante liefert die Anzeigefelder.
            if (fields > existing._best) {
                existing.name = reading.name;
                existing.address = reading.address;
                existing.contactPerson = reading.contactPerson;
                existing.phone = reading.phone;
                existing.email = reading.email;
                existing._best = fields;
            }
        }
    }

    return Array.from(byKey.values()).map(({ _best, ...c }) => c);
};

// ─── Abgleich-Zeilen ─────────────────────────────────────────────────────────

export type OnboardingSource = 'projects' | 'list';

/**
 * new                — nur eine Quelle, kein Bestandstreffer: anlegen.
 * merged_sources     — Projekt UND Kundenliste, gleicher Normalname: EIN Kunde.
 * existing_match     — Kundennummer oder Normalname trifft einen Bestandskunden.
 * number_conflict    — die Nummer der Liste gehoert einem ANDEREN Kunden.
 * ambiguous          — aehnlich, aber nicht sicher (Bestand oder andere Zeile).
 * duplicate_in_source— Name/Nummer kam in derselben Quelle schon vor.
 */
export type OnboardingClass =
    | 'new'
    | 'merged_sources'
    | 'existing_match'
    | 'number_conflict'
    | 'ambiguous'
    | 'duplicate_in_source';

export type OnboardingAction = 'create' | 'update' | 'skip';

/**
 * Vorschau, welche Kundennummer die Zeile am Ende traegt.
 *
 * list   — Nummer aus der Kundenliste (Steuerkanzlei), wird gesetzt.
 * keep   — der Bestandskunde behaelt seine Nummer.
 * auto   — wird beim Anlegen automatisch aus dem Buchstaben-Bereich vergeben.
 * manual — kein Bereich bestimmbar, muss von Hand vergeben werden.
 */
export type NumberPreview =
    | { kind: 'list'; number: string }
    | { kind: 'keep'; number: string }
    | { kind: 'auto'; range: NumberRange }
    | { kind: 'manual' };

export interface OnboardingRow {
    /** Stabile, deterministische Zeilen-ID (Testbarkeit + React-Key). */
    id: string;
    key: string;
    sources: OnboardingSource[];
    name: string;
    customerNumber?: string;
    address?: string;
    street?: string;
    zip?: string;
    city?: string;
    email?: string;
    phone?: string;
    contactPerson?: string;
    customerType?: 'company' | 'person';
    salutation?: string;
    title?: string;
    firstName?: string;
    lastName?: string;
    origins: ProjectCompanyOrigin[];
    rowClass: OnboardingClass;
    /** Vorschlag — im Review-Schritt je Zeile aenderbar. */
    action: OnboardingAction;
    /** Bestandskunde bei existing_match / number_conflict / ambiguous. */
    existing?: Customer;
    /**
     * Nummer der Kundenliste auf den Bestandskunden uebernehmen (nur bei
     * existing_match mit abweichender Nummer relevant).
     */
    takeNumber: boolean;
    /** Deutschsprachige Hinweise fuer die Review-Tabelle. */
    hints: string[];
    /** id der Zeile, auf die eine Dublette/Aehnlichkeit zeigt. */
    relatedRowId?: string;
    /** Aehnliche Bestandskunden (nur bei ambiguous). */
    similarTo?: { id: string; name: string; score: number }[];
}

export interface OnboardingPlanInput {
    projectCompanies?: ProjectCompanyCandidate[];
    listRows?: ImportedCustomerRow[];
    existing: Customer[];
}

/**
 * Ab dieser Namensaehnlichkeit gilt eine Zeile als "koennte dasselbe sein".
 * Gleicher Wert wie im Listen-Import (customerListImport.ts) und in der
 * Dublettenpruefung — bewusst kein eigener Schwellwert.
 */
const SIMILAR_MIN = 0.5;

const clean = (v: string | undefined): string | undefined => {
    const t = (v ?? '').trim();
    return t || undefined;
};

const fill = (target: Record<string, any>, field: string, value: string | undefined) => {
    if (!target[field] && clean(value)) target[field] = clean(value);
};

const baseRow = (id: string, key: string, name: string): OnboardingRow => ({
    id,
    key,
    sources: [],
    name,
    origins: [],
    rowClass: 'new',
    action: 'create',
    takeNumber: false,
    hints: [],
});

const rowFromList = (r: ImportedCustomerRow, index: number): OnboardingRow => {
    const row = baseRow(`list-${index}`, normalizeCompanyName(r.name || ''), r.name);
    row.sources = ['list'];
    row.customerNumber = clean(r.customerNumber);
    row.address = clean(r.address);
    row.street = clean(r.street);
    row.zip = clean(r.zip);
    row.city = clean(r.city);
    row.email = clean(r.email);
    row.phone = clean(r.phone);
    row.customerType = r.customerType;
    row.salutation = clean(r.salutation);
    row.title = clean(r.title);
    row.firstName = clean(r.firstName);
    row.lastName = clean(r.lastName);
    return row;
};

const rowFromProject = (c: ProjectCompanyCandidate): OnboardingRow => {
    const row = baseRow(`proj-${c.key}`, c.key, c.name);
    row.sources = ['projects'];
    row.address = clean(c.address);
    row.email = clean(c.email);
    row.phone = clean(c.phone);
    row.contactPerson = clean(c.contactPerson);
    row.origins = [...c.origins];
    return row;
};

/** Projektdaten in eine bestehende (Listen-)Zeile einhaengen: nur Luecken. */
const mergeProjectInto = (row: OnboardingRow, c: ProjectCompanyCandidate): void => {
    if (!row.sources.includes('projects')) row.sources.push('projects');
    row.origins.push(...c.origins);
    fill(row, 'address', c.address);
    fill(row, 'email', c.email);
    fill(row, 'phone', c.phone);
    fill(row, 'contactPerson', c.contactPerson);
};

/**
 * Blocking-Schluessel fuer den paarweisen Aehnlichkeitsvergleich: jedes Token
 * plus der 4-Zeichen-Praefix des Normalnamens. Ohne das waere der Vergleich
 * O(n²) ueber die ganze Kanzleiliste; der Praefix faengt zusaetzlich die
 * Tippfehler-Faelle ("Mueler"/"Mueller"), die kein Token teilen.
 */
const blockingKeys = (key: string): string[] => {
    const tokens = key.split(' ').filter(Boolean);
    const prefix = key.replace(/\s+/g, '').slice(0, 4);
    return prefix ? [...tokens, `#${prefix}`] : tokens;
};

/**
 * Welche Nummer traegt die Zeile am Ende? Wird live berechnet — Aktion und
 * takeNumber sind im Review-Schritt aenderbar, ein eingefrorener Wert wuerde
 * dem Nutzer nach jedem Umschalten etwas Falsches zeigen.
 */
export const previewCustomerNumber = (row: OnboardingRow): NumberPreview => {
    if (row.action === 'update' && row.existing) {
        if (row.takeNumber && row.customerNumber) return { kind: 'list', number: row.customerNumber };
        if (row.existing.customerNumber) return { kind: 'keep', number: row.existing.customerNumber };
        // Bestandskunde ohne Nummer: beim Aktualisieren vergibt NICHTS
        // automatisch eine — das bleibt Handarbeit.
        return { kind: 'manual' };
    }
    if (row.customerNumber && row.rowClass !== 'number_conflict') {
        // Nummernkonflikt: die Nummer der Liste ist vergeben, angelegt wird mit
        // der naechsten freien aus dem Buchstaben-Bereich.
        return { kind: 'list', number: row.customerNumber };
    }
    const range = rangeForName(row.name);
    return range ? { kind: 'auto', range } : { kind: 'manual' };
};

/**
 * Der Kern: Projekt-Kandidaten, Listenzeilen und Bestandskunden zu
 * Abgleich-Zeilen mit Vorschlagsaktion. Legt NICHTS an — das Ergebnis ist
 * reine Entscheidungsgrundlage.
 */
export const buildOnboardingPlan = (input: OnboardingPlanInput): OnboardingRow[] => {
    const existing = input.existing || [];
    const rows: OnboardingRow[] = [];
    const byKey = new Map<string, OnboardingRow>();
    const byNumber = new Map<string, OnboardingRow>();

    // (1) Kundenliste zuerst: sie traegt die autoritativen Nummern, und bei
    // Aehnlichkeiten wird spaeter die SPAETERE Zeile zurueckgestellt.
    (input.listRows || []).forEach((r, index) => {
        const row = rowFromList(r, index);
        if (!row.key && !clean(r.name)) return;
        const numKey = normNumber(row.customerNumber);
        const dup = (row.key ? byKey.get(row.key) : undefined) ?? (numKey ? byNumber.get(numKey) : undefined);
        if (dup) {
            row.rowClass = 'duplicate_in_source';
            row.action = 'skip';
            row.relatedRowId = dup.id;
            row.hints.push(`Kommt in der Kundenliste doppelt vor (wie „${dup.name}“) — wird übersprungen.`);
            rows.push(row);
            return;
        }
        if (row.key) byKey.set(row.key, row);
        if (numKey) byNumber.set(numKey, row);
        rows.push(row);
    });

    // (2) Projekt-Unternehmen: gleicher Normalname -> dieselbe Zeile.
    for (const c of input.projectCompanies || []) {
        const target = c.key ? byKey.get(c.key) : undefined;
        if (target && target.rowClass !== 'duplicate_in_source') {
            mergeProjectInto(target, c);
            target.rowClass = 'merged_sources';
            target.hints.push('Steht in der Kundenliste UND in Projekten — wird als ein Kunde angelegt.');
            continue;
        }
        const row = rowFromProject(c);
        if (row.key) byKey.set(row.key, row);
        rows.push(row);
    }

    // (3) Abgleich gegen den Bestand.
    const existingByNumber = new Map<string, Customer>();
    const existingByKey = new Map<string, Customer>();
    for (const c of existing) {
        const n = normNumber(c.customerNumber);
        if (n && !existingByNumber.has(n)) existingByNumber.set(n, c);
        const k = normalizeCompanyName(c.name || '');
        if (k && !existingByKey.has(k)) existingByKey.set(k, c);
    }

    for (const row of rows) {
        if (row.rowClass === 'duplicate_in_source') continue;
        const numKey = normNumber(row.customerNumber);
        const numberHit = numKey ? existingByNumber.get(numKey) : undefined;

        const nameHit = row.key ? existingByKey.get(row.key) : undefined;

        if (numberHit) {
            if (nameSimilarity(row.name, numberHit.name) < SIMILAR_MIN) {
                // Fremder Nummernkreis: die Nummer gehoert jemand anderem.
                if (nameHit && nameHit.id !== numberHit.id) {
                    // Der Kunde selbst ist da, nur die Nummer der Kanzlei ist
                    // hier schon anderweitig vergeben: aktualisieren, aber die
                    // Nummer NICHT anfassen (applyExistingMatch prueft das).
                    row.hints.push(
                        `Kundennummer ${row.customerNumber} aus der Kundenliste gehört hier „${numberHit.name}“.`,
                    );
                    applyExistingMatch(row, nameHit, existing);
                    continue;
                }
                row.rowClass = 'number_conflict';
                row.action = 'skip';
                row.existing = numberHit;
                row.hints.push(
                    `Kundennummer ${row.customerNumber} ist bereits an „${numberHit.name}“ vergeben. ` +
                        'Beim Anlegen wird die nächste freie Nummer vergeben.',
                );
                continue;
            }
            applyExistingMatch(row, numberHit, existing);
            continue;
        }

        if (nameHit) {
            applyExistingMatch(row, nameHit, existing);
            continue;
        }

        const hits = findSimilarCustomers(
            { name: row.name, email: row.email, zip: row.zip, city: row.city, street: row.street },
            existing,
        );
        if (hits.length) {
            row.rowClass = 'ambiguous';
            row.action = 'skip';
            row.existing = hits[0].customer;
            row.similarTo = hits.map((h) => ({ id: h.customer.id, name: h.customer.name, score: h.score }));
            row.hints.push(
                `Ähnlich zum Bestandskunden „${hits[0].customer.name}“ (${hits[0].reasons.join(', ')}) — ` +
                    'bitte entscheiden: zusammenführen oder trotzdem anlegen.',
            );
        }
    }

    // (4) Aehnliche Zeilen UNTEREINANDER: kein Auto-Merge, die spaetere Zeile
    // wird zurueckgestellt und benennt ihre Partnerzeile.
    const blocks = new Map<string, OnboardingRow[]>();
    for (const row of rows) {
        if (row.rowClass !== 'new' && row.rowClass !== 'merged_sources') continue;
        const partners = new Set<OnboardingRow>();
        for (const bk of blockingKeys(row.key)) {
            for (const other of blocks.get(bk) || []) partners.add(other);
        }
        for (const other of partners) {
            if (nameSimilarity(row.name, other.name) < SIMILAR_MIN) continue;
            row.rowClass = 'ambiguous';
            row.action = 'skip';
            row.relatedRowId = other.id;
            row.hints.push(
                `Ähnlich zu „${other.name}“ in derselben Übernahme — kein automatisches ` +
                    'Zusammenführen. Bitte prüfen und ggf. anlegen.',
            );
            break;
        }
        if (row.rowClass !== 'new' && row.rowClass !== 'merged_sources') continue;
        for (const bk of blockingKeys(row.key)) {
            const list = blocks.get(bk);
            if (list) list.push(row);
            else blocks.set(bk, [row]);
        }
    }

    // (5) Restwarnungen zur Nummernvergabe.
    for (const row of rows) {
        if (row.action === 'create' && previewCustomerNumber(row).kind === 'manual') {
            row.hints.push(
                'Aus dem Namen lässt sich kein Nummernkreis bestimmen — die Kundennummer muss ' +
                    'nach dem Anlegen manuell vergeben werden.',
            );
        }
    }

    return rows;
};

/** Treffer auf einen Bestandskunden: aktualisieren statt anlegen. */
const applyExistingMatch = (row: OnboardingRow, hit: Customer, existing: Customer[]): void => {
    row.rowClass = 'existing_match';
    row.action = 'update';
    row.existing = hit;
    if (hit.archivedAt) {
        row.hints.push(`„${hit.name}“ ist archiviert — die Aktualisierung holt ihn NICHT zurück.`);
    }
    const listNum = normNumber(row.customerNumber);
    const existingNum = normNumber(hit.customerNumber);
    if (!listNum || listNum === existingNum) return;
    if (numberTakenByOther(row.customerNumber, existing, hit.id)) {
        row.takeNumber = false;
        row.hints.push(
            `Kundennummer ${row.customerNumber} ist bereits an einen anderen Kunden vergeben — ` +
                'sie wird NICHT übernommen.',
        );
        return;
    }
    // Die Kanzlei fuehrt den Nummernkreis: ihre Nummer gewinnt.
    row.takeNumber = true;
    row.hints.push(
        existingNum
            ? `Kundennummer wird von ${hit.customerNumber} auf ${row.customerNumber} (Kundenliste) geändert.`
            : `Kundennummer ${row.customerNumber} aus der Kundenliste wird übernommen.`,
    );
};

// ─── Projekt-Verknuepfung ────────────────────────────────────────────────────

export interface ProjectLinkPlan {
    /** Zeilen-ID -> Projekt-IDs, die verknuepft werden. */
    byRow: Map<string, string[]>;
    /** Projekte, die bewusst NICHT verknuepft werden, mit Begruendung. */
    skipped: { rowId: string; projectId: string; reason: string }[];
}

/**
 * Welche Projekte werden am Ende auf welche Zeile verknuepft?
 *
 * Regeln:
 *  - nur Zeilen mit Aktion anlegen/zusammenfuehren,
 *  - nur Herkuenfte mit linkable (Projekt haengt an keinem Kunden),
 *  - NUR die Auftraggeber-Rolle. Ein Bauherr ist nicht der Auftraggeber, auch
 *    dann nicht, wenn das Projekt gar keinen fuehrt: customer_id ist die
 *    Rechnungsbeziehung, und wer die Rechnung bekommt, ist eine Angabe und
 *    keine Vermutung. Der Bauherr wird als Firma angelegt, das Projekt bleibt
 *    unverknuepft und wird im Projekt selbst zugewiesen.
 *  - ein Projekt genau EINMAL: bei zwei Zeilen gewinnt die fruehere. Sonst
 *    wuerde die letzte Schreiboperation zufaellig bestimmen, wem es gehoert.
 */
export const planProjectLinks = (rows: OnboardingRow[]): ProjectLinkPlan => {
    // Erste beanspruchende Zeile gewinnt; ein zweiter Anspruch wird gemeldet.
    const claim = new Map<string, { rowId: string }>();
    const skipped: { rowId: string; projectId: string; reason: string }[] = [];

    rows.forEach((row) => {
        if (row.action === 'skip') return;
        for (const o of row.origins) {
            if (!o.linkable) {
                skipped.push({
                    rowId: row.id,
                    projectId: o.projectId,
                    reason: 'Projekt ist bereits einem Kunden zugeordnet',
                });
                continue;
            }
            if (o.role !== 'auftraggeber') {
                skipped.push({
                    rowId: row.id,
                    projectId: o.projectId,
                    reason: 'Bauherr ist nicht der Auftraggeber – Projekt bleibt unverknuepft',
                });
                continue;
            }
            const held = claim.get(o.projectId);
            if (!held) {
                claim.set(o.projectId, { rowId: row.id });
                continue;
            }
            if (held.rowId === row.id) continue;
            skipped.push({
                rowId: row.id,
                projectId: o.projectId,
                reason: 'Projekt wird einer anderen Zeile zugeordnet',
            });
        }
    });

    const byRow = new Map<string, string[]>();
    for (const [projectId, held] of claim) {
        const list = byRow.get(held.rowId);
        if (list) list.push(projectId);
        else byRow.set(held.rowId, [projectId]);
    }
    for (const list of byRow.values()) list.sort();
    return { byRow, skipped };
};

// ─── Nutzlasten ──────────────────────────────────────────────────────────────

export type NewCustomerInput = Omit<Customer, 'id' | 'createdBy' | 'createdAt' | 'updatedAt'>;

/**
 * Anlage-Nutzlast. Die Nummer der Kundenliste wird mitgegeben (autoritativ);
 * beim Nummernkonflikt bleibt sie WEG, damit die Anlage die naechste freie
 * Nummer aus dem Buchstaben-Bereich vergibt statt an der Eindeutigkeit zu
 * scheitern oder eine fremde Nummer zu ueberschreiben.
 */
export const buildCreateInput = (
    row: OnboardingRow,
    makeId: () => string = () => crypto.randomUUID(),
): NewCustomerInput => ({
    name: row.name,
    customerNumber: row.rowClass === 'number_conflict' ? undefined : row.customerNumber,
    address: row.address,
    street: row.street,
    zip: row.zip,
    city: row.city,
    email: row.email,
    phone: row.phone,
    customerType: row.customerType,
    salutation: row.salutation,
    title: row.title,
    firstName: row.firstName,
    lastName: row.lastName,
    contacts: row.contactPerson
        ? [{ id: makeId(), name: row.contactPerson, phone: row.phone, email: row.email }]
        : [],
    // Kein Zahlungsziel aus dem Import — siehe Customer.paymentTermsDays:
    // ohne Eintrag gilt die Grundeinstellung des Rechnungsmoduls.
    defaultDiscountPercent: 0,
});

const TEXT_FIELDS = [
    'address',
    'street',
    'zip',
    'city',
    'email',
    'phone',
    'salutation',
    'title',
    'firstName',
    'lastName',
] as const;

/**
 * Update-Nutzlast fuer einen Bestandstreffer: NUR leere Felder werden gefuellt.
 *
 * Ein Bestandskunde ist gepflegt — eine Kanzleiliste oder ein alter
 * Projekt-Kopf darf ihn nicht ueberschreiben. Einzige Ausnahme ist die
 * Kundennummer, und die nur, wenn der Nutzer sie in der Zeile stehen laesst
 * (takeNumber). Der Anzeigename bleibt IMMER unberuehrt.
 */
export const buildUpdateInput = (row: OnboardingRow, existing: Customer): Partial<Customer> => {
    const updates: Partial<Customer> = {};
    for (const field of TEXT_FIELDS) {
        const value = clean(row[field] as string | undefined);
        if (!value) continue;
        if (clean(existing[field] as string | undefined)) continue;
        (updates as Record<string, string>)[field] = value;
    }
    if (updates.street || updates.zip || updates.city) {
        const street = updates.street ?? existing.street;
        const zip = updates.zip ?? existing.zip;
        const city = updates.city ?? existing.city;
        const place = [zip, city].filter(Boolean).join(' ');
        const composed = [street, place].filter(Boolean).join(', ');
        if (composed && !clean(existing.address)) updates.address = composed;
    }
    if (row.customerType && !existing.customerType) updates.customerType = row.customerType;
    if (row.contactPerson && !(existing.contacts || []).length) {
        updates.contacts = [
            {
                id: crypto.randomUUID(),
                name: row.contactPerson,
                phone: row.phone ?? existing.phone,
                email: row.email ?? existing.email,
            },
        ];
    }
    if (
        row.takeNumber &&
        row.customerNumber &&
        normNumber(row.customerNumber) !== normNumber(existing.customerNumber)
    ) {
        updates.customerNumber = row.customerNumber;
    }
    return updates;
};

// ─── Zusammenfassung ─────────────────────────────────────────────────────────

export interface PlanSummary {
    create: number;
    update: number;
    skip: number;
    link: number;
    conflicts: number;
}

export const summarizePlan = (rows: OnboardingRow[], links?: ProjectLinkPlan): PlanSummary => {
    const plan = links ?? planProjectLinks(rows);
    let link = 0;
    for (const ids of plan.byRow.values()) link += ids.length;
    return {
        create: rows.filter((r) => r.action === 'create').length,
        update: rows.filter((r) => r.action === 'update').length,
        skip: rows.filter((r) => r.action === 'skip').length,
        link,
        conflicts: rows.filter((r) => r.rowClass === 'number_conflict').length,
    };
};

// ─── Ausfuehrung ─────────────────────────────────────────────────────────────

export interface OnboardingDeps {
    createCustomer: (input: NewCustomerInput) => Promise<{ id: string }>;
    updateCustomer: (id: string, updates: Partial<Customer>) => Promise<void>;
    linkProjects: (customerId: string, projectIds: string[]) => Promise<void>;
    onProgress?: (done: number, total: number) => void;
    makeId?: () => string;
}

export type OnboardingRowStatus = 'created' | 'updated' | 'unchanged' | 'skipped' | 'failed';

export interface OnboardingRowResult {
    rowId: string;
    name: string;
    status: OnboardingRowStatus;
    customerId?: string;
    linkedProjectIds: string[];
    /** Zeile fehlgeschlagen — nichts angelegt/geaendert. */
    error?: string;
    /** Kunde ok, aber ein Teilschritt hat nicht geklappt. */
    warning?: string;
}

export interface OnboardingResult {
    rows: OnboardingRowResult[];
    created: number;
    updated: number;
    unchanged: number;
    skipped: number;
    failed: number;
    linked: number;
}

/** Postgres-Eindeutigkeitsverletzung (Kundennummer ist unique). */
export const isUniqueViolation = (e: unknown): boolean => {
    if (!e || typeof e !== 'object') return false;
    const code = (e as { code?: unknown }).code;
    if (code === '23505') return true;
    const message = String((e as { message?: unknown }).message ?? '');
    return /23505|duplicate key/i.test(message);
};

/**
 * Plan ausfuehren. Ein Fehler blockiert IMMER nur seine eigene Zeile — der
 * Rest laeuft weiter, sonst bliebe eine halb uebernommene Liste zurueck, deren
 * Rest beim zweiten Anlauf in dieselbe Kollision liefe.
 *
 * Reihenfolge je Zeile: erst der Kunde, dann die Projekte. Scheitert die
 * Verknuepfung, steht der Kunde trotzdem — das ist eine Warnung an der Zeile,
 * kein Fehlschlag.
 */
export const executeOnboardingPlan = async (
    rows: OnboardingRow[],
    deps: OnboardingDeps,
): Promise<OnboardingResult> => {
    const links = planProjectLinks(rows);
    const todo = rows.filter((r) => r.action !== 'skip');
    const results: OnboardingRowResult[] = [];
    let done = 0;

    for (const row of rows) {
        if (row.action === 'skip') {
            results.push({ rowId: row.id, name: row.name, status: 'skipped', linkedProjectIds: [] });
            continue;
        }

        const result: OnboardingRowResult = {
            rowId: row.id,
            name: row.name,
            status: 'failed',
            linkedProjectIds: [],
        };

        try {
            if (row.action === 'create') {
                const created = await deps.createCustomer(buildCreateInput(row, deps.makeId));
                result.customerId = created.id;
                result.status = 'created';
            } else {
                const existing = row.existing;
                if (!existing) {
                    throw new Error('Kein Bestandskunde hinterlegt — Zeile kann nicht zusammengeführt werden.');
                }
                result.customerId = existing.id;
                const updates = buildUpdateInput(row, existing);
                if (!Object.keys(updates).length) {
                    result.status = 'unchanged';
                } else {
                    try {
                        await deps.updateCustomer(existing.id, updates);
                        result.status = 'updated';
                    } catch (e) {
                        // Nummer schon vergeben: die uebrigen Felder duerfen
                        // trotzdem landen — die Nummer bleibt dann beim alten
                        // Stand und die Zeile traegt die Warnung.
                        if (!isUniqueViolation(e) || updates.customerNumber === undefined) throw e;
                        const { customerNumber, ...rest } = updates;
                        if (Object.keys(rest).length) {
                            await deps.updateCustomer(existing.id, rest);
                            result.status = 'updated';
                        } else {
                            result.status = 'unchanged';
                        }
                        result.warning =
                            `Kundennummer ${customerNumber} ist bereits vergeben und wurde nicht übernommen.`;
                    }
                }
            }
        } catch (e) {
            result.status = 'failed';
            result.error = isUniqueViolation(e)
                ? row.customerNumber
                    ? `Kundennummer ${row.customerNumber} ist bereits vergeben.`
                    : 'Die Kundennummer ist bereits vergeben.'
                : describeError(e);
            results.push(result);
            done += 1;
            deps.onProgress?.(done, todo.length);
            continue;
        }

        const projectIds = links.byRow.get(row.id) ?? [];
        if (projectIds.length && result.customerId) {
            try {
                await deps.linkProjects(result.customerId, projectIds);
                result.linkedProjectIds = projectIds;
            } catch (e) {
                result.warning = [result.warning, `Projekte konnten nicht verknüpft werden: ${describeError(e)}`]
                    .filter(Boolean)
                    .join(' ');
            }
        }

        results.push(result);
        done += 1;
        deps.onProgress?.(done, todo.length);
    }

    return {
        rows: results,
        created: results.filter((r) => r.status === 'created').length,
        updated: results.filter((r) => r.status === 'updated').length,
        unchanged: results.filter((r) => r.status === 'unchanged').length,
        skipped: results.filter((r) => r.status === 'skipped').length,
        failed: results.filter((r) => r.status === 'failed').length,
        linked: results.reduce((s, r) => s + r.linkedProjectIds.length, 0),
    };
};
